import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { petGuardianshipAPI } from '../../services/api';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import { getUserPlaceholderImage } from '../../utils/imageUtils';

const GuardianshipRequests = ({ onRequestsUpdated }) => {
    const { t } = useTranslation();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [highlightId, setHighlightId] = useState(() => sessionStorage.getItem('highlightGuardianshipRequestId'));

    useEffect(() => {
        if (highlightId) {
            sessionStorage.removeItem('highlightGuardianshipRequestId');
            const timer = setTimeout(() => setHighlightId(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [highlightId]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const response = await petGuardianshipAPI.getMyGuardianshipRequests();
            setRequests(response.data.data || response.data || []);
        } catch (error) {
            console.error('Error fetching guardianship requests:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleRespond = async (requestId, action) => {
        setProcessing(requestId);
        try {
            await petGuardianshipAPI.respondToRequest(requestId, action);
            showSuccessToast(t('common.success'));
            await fetchRequests();
            if (onRequestsUpdated) onRequestsUpdated();
        } catch (error) {
            console.error(`Error ${action} request:`, error);
            showErrorToast(t('common.error'));
        } finally {
            setProcessing(null);
        }
    };

    if (loading || requests.length === 0) {
        return null; // Don't show anything if no requests
    }

    return (
        <div className="page-frame mypets-section mypets-section-responsive" style={{ marginBottom: '20px' }}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #E5E7EB', borderLeft: '4px solid #8548A8' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 8px 0', color: '#111827' }}>
                    {t('petProfile.guardians.requests.title')}
                </h3>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>
                    {t('petProfile.guardians.requests.description')}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {requests.map((request) => {
                        const isHighlighted = highlightId === String(request.id);
                        return (
                        <div key={request.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px',
                            background: isHighlighted ? '#F3E8FF' : '#F9FAFB',
                            borderRadius: '8px',
                            border: isHighlighted ? '2px solid #8548A8' : '1px solid #F3F4F6',
                            transition: 'all 0.5s ease',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: '200px' }}>
                                <div style={{ marginRight: '12px', position: 'relative' }}>
                                    {/* Pet Image or Placeholder */}
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '8px', overflow: 'hidden', background: '#e5e7eb',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#9ca3af'
                                    }}>
                                        {request.pet?.name ? request.pet.name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>
                                        {t('petProfile.guardians.guardian')} {t('common.for')} {request.pet?.name || 'Unknown Pet'}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#6B7280' }}>
                                        {t('common.from')}: {(() => {
                                            const requester = request.requester || request.owner || request.user || request.inviter;
                                            if (!requester) return 'Unknown User';
                                            const firstName = requester.first_name || requester.firstName || '';
                                            const lastName = requester.last_name || requester.lastName || '';
                                            const fullName = `${firstName} ${lastName}`.trim();
                                            return fullName || requester.name || requester.full_name || requester.display_name || requester.username || 'Unknown User';
                                        })()}
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => handleRespond(request.id, 'accepted')}
                                    disabled={!!processing}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#10B981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        cursor: !!processing ? 'default' : 'pointer',
                                        opacity: !!processing ? 0.7 : 1
                                    }}
                                >
                                    {processing === request.id ? '...' : t('petProfile.guardians.requests.accept')}
                                </button>
                                <button
                                    onClick={() => handleRespond(request.id, 'rejected')}
                                    disabled={!!processing}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#EF4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        cursor: !!processing ? 'default' : 'pointer',
                                        opacity: !!processing ? 0.7 : 1
                                    }}
                                >
                                    {processing === request.id ? '...' : t('petProfile.guardians.requests.reject')}
                                </button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default GuardianshipRequests;
